import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, ClipboardList, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { InternalMovementView, MovementTypeView, DepartmentSelectionView, MovementTaskView, MovementTaskInput, UpdateTaskStatusInput } from '../../../models/employee';
import { MovementStatus, ProcessTaskStatus } from '../../../models/employee';
import { updateMovement, getMovementDepartments, getInternalMovement, updateMovementTaskStatus, addMovementTask, removeMovementTask } from '../../../api/Employee/internalMovements';
import { getMovementTypes } from '../../../api/Employee/lifecycleSettings';
import { appDate } from '../../../utils/appDateFormat';
import { DateField } from '../../../components/dateFields';
import { apiDateOnlyParam } from '@/utils/apiDate';

interface EditMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  movement: InternalMovementView | null;
}

interface FormData {
  movementTypeId: number;
  newPosition: string;
  newDepartmentId: number | undefined;
  effectiveDate: string;
  reason: string;
}

const statusOptions = [
  { value: ProcessTaskStatus.Pending, label: 'Pending' },
  { value: ProcessTaskStatus.InProgress, label: 'In Progress' },
  { value: ProcessTaskStatus.Completed, label: 'Completed' },
  { value: ProcessTaskStatus.Cancelled, label: 'Cancelled' },
];

const EditMovementModal: React.FC<EditMovementModalProps> = ({ isOpen, onClose, onSubmit, movement }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [movementTypes, setMovementTypes] = useState<MovementTypeView[]>([]);
  const [departments, setDepartments] = useState<DepartmentSelectionView[]>([]);
  const [tasks, setTasks] = useState<MovementTaskView[]>([]);
  const [newItemName, setNewItemName] = useState('');
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    movementTypeId: 0,
    newPosition: '',
    newDepartmentId: undefined,
    effectiveDate: '',
    reason: '',
  });

  const isDisabled = movement != null && (
    movement.status === MovementStatus.Completed
    || movement.status === MovementStatus.Cancelled
  );

  useEffect(() => {
    if (isOpen) {
      getMovementTypes().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setMovementTypes((result.payload || []).filter(mt => mt.isActive));
        }
      });
      getMovementDepartments().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setDepartments(result.payload || []);
        }
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (movement && isOpen) {
      setFormData({
        movementTypeId: movement.movementTypeId,
        newPosition: movement.newPosition || '',
        newDepartmentId: movement.newDepartmentId ?? undefined,
        effectiveDate: apiDateOnlyParam(movement.effectiveDate),
        reason: movement.reason || '',
      });

      // Load fresh movement data for tasks
      getInternalMovement(movement.id).then(result => {
        if (!result.errors || result.errors.length === 0) {
          const m = result.payload;
          if (m) {
            setTasks(m.tasks || []);
          }
        }
      }).catch(() => {
        setTasks(movement.tasks || []);
      });
    }
  }, [movement, isOpen]);

  const handleUpdateTaskStatus = async (taskId: number, newStatus: ProcessTaskStatus) => {
    const input: UpdateTaskStatusInput = { status: newStatus };
    try {
      const result = await updateMovementTaskStatus(taskId, input);
      if (!result.errors || result.errors.length === 0) {
        if (result.payload) {
          setTasks(prev => prev.map(t => t.id === taskId ? result.payload! : t));
        }
      }
    } catch {
      // silently fail
    }
  };

  const handleAddItem = async () => {
    if (!newItemName?.trim() || !movement) return;

    const input: MovementTaskInput = {
      name: newItemName,
      mandatory: true,
      isBlocking: false,
      dueDate: formData.effectiveDate || undefined,
      sortOrder: tasks.length,
    };

    try {
      const result = await addMovementTask(movement.id, input);
      if (!result.errors || result.errors.length === 0) {
        setTasks(prev => [...prev, result.payload]);
        setNewItemName('');
      }
    } catch {
      // silently fail
    }
  };

  const handleDeleteItem = async (taskId: number) => {
    try {
      const result = await removeMovementTask(taskId);
      if (!result.errors || result.errors.length === 0) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
      }
    } catch {
      // silently fail
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!movement || isDisabled) return;
    setSubmitting(true);
    try {
      const result = await updateMovement(movement.id, {
        movementTypeId: formData.movementTypeId,
        newPosition: formData.newPosition,
        newDepartmentId: formData.newDepartmentId,
        effectiveDate: formData.effectiveDate,
        reason: formData.reason || undefined,
      });
      if (!result.errors || result.errors.length === 0) {
        onSubmit();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: ProcessTaskStatus): string => {
    switch (status) {
      case ProcessTaskStatus.Completed: return 'bg-green-100 text-green-800';
      case ProcessTaskStatus.Cancelled: return 'bg-red-100 text-red-800';
      case ProcessTaskStatus.InProgress: return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Edit Internal Movement')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('Update movement details for')} {movement?.employeeName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isDisabled && (
          <div className="mx-6 mt-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">{t('This movement cannot be edited in its current state.')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Details (Read-only) */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <div>
              <span className="text-xs text-gray-500">{t('Employee')}</span>
              <div className="text-sm font-medium text-gray-900">{movement?.employeeName}</div>
            </div>
          </div>

          {/* Movement Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Movement Type')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              disabled={isDisabled}
              value={formData.movementTypeId || ''}
              onChange={(e) => setFormData({ ...formData, movementTypeId: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
            >
              <option value="">{t('Select movement type...')}</option>
              {movementTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {/* New Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('New Position')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              disabled={isDisabled}
              value={formData.newPosition}
              onChange={(e) => setFormData({ ...formData, newPosition: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* New Department */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('New Department')}
            </label>
            <select
              disabled={isDisabled}
              value={formData.newDepartmentId || ''}
              onChange={(e) => setFormData({ ...formData, newDepartmentId: e.target.value ? parseInt(e.target.value) : undefined })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
            >
              <option value="">{t('Select department...')}</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Effective Date')} <span className="text-red-500">*</span>
            </label>
            <DateField
              required
              disabled={isDisabled}
              value={formData.effectiveDate}
              onChange={(v) => setFormData({ ...formData, effectiveDate: v })}
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Reason')}
            </label>
            <textarea
              rows={3}
              disabled={isDisabled}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
              placeholder={t('Provide reason for this movement...')}
            />
          </div>

          {/* Movement Checklist Section */}
          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-gray-900">{t('Movement Checklist')}</h4>
              <span className="text-sm text-gray-600">{tasks.length} {t('items')}</span>
            </div>

            {/* Add New Item */}
            {!isDisabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-end space-x-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('Item Name')}</label>
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder={t('Enter checklist item name...')}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('Add Item')}
                  </button>
                </div>
              </div>
            )}

            {/* Checklist Items */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {tasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('No checklist items yet. Add your first item above.')}</p>
                </div>
              ) : (
                tasks.map((item) => (
                  <div key={item.id} className="bg-card border border-border rounded-lg p-3 hover:border-border transition-colors">
                    <div className="flex items-start space-x-3">
                      {/* Status Dropdown */}
                      <select
                        value={item.status}
                        onChange={(e) => handleUpdateTaskStatus(item.id, e.target.value as ProcessTaskStatus)}
                        className={`px-2 py-1 text-xs font-medium rounded border-0 focus:outline-none focus:ring-2 focus:ring-primary ${getStatusColor(item.status)}`}
                      >
                        {statusOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                        ))}
                      </select>

                      {/* Item Content */}
                      <div className="flex-1">
                        {editingItemId === item.id ? (
                          <input
                            type="text"
                            defaultValue={item.name}
                            onBlur={() => setEditingItemId(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setEditingItemId(null);
                              }
                            }}
                            autoFocus
                            className="w-full px-2 py-1 border border-primary rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        ) : (
                          <div
                            className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary"
                            onClick={() => setEditingItemId(item.id)}
                          >
                            {item.name}
                          </div>
                        )}
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-600">{item.typeName || ''}</span>
                          {item.dueDate && (
                            <span className="text-xs text-gray-500">{t('Due')}: {appDate(item.dueDate)}</span>
                          )}
                          {item.mandatory && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{t('Required')}</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {!isDisabled && (
                        <div className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => setEditingItemId(item.id)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title={t('Edit item')}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('Delete item')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              {t('Cancel')}
            </button>
            {!isDisabled && (
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {submitting && <Loader className="w-4 h-4 mr-2 animate-spin" />}
                {t('Save Changes')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMovementModal;
